import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAdminCustomerDetail } from '@/api/hooks/useAdminCustomers';
import { useAddCustomerFlag, useRemoveCustomerFlag } from '@/api/hooks/useCustomerFlags';
import { useQueryClient } from '@tanstack/react-query';
import { formatCurrency } from '@/lib/format';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { User, Flag, X, ChevronDown, ChevronUp } from 'lucide-react';
import { toast } from 'sonner';

const FLAG_COLORS: Record<string, string> = {
  BANNED: 'bg-red-500 text-white',
  NO_SHOW_RISK: 'bg-orange-500 text-white',
  REQUIRES_DEPOSIT: 'bg-amber-500 text-white',
  VIP: 'bg-yellow-400 text-black',
  WATCH: 'bg-blue-500 text-white',
};
const FLAG_TYPES = ['BANNED', 'NO_SHOW_RISK', 'REQUIRES_DEPOSIT', 'VIP', 'WATCH'] as const;

const STATUS_COLORS: Record<string, string> = {
  CONFIRMED: 'bg-blue-100 text-blue-800',
  ACTIVE: 'bg-green-100 text-green-800',
  COMPLETED: 'bg-slate-100 text-slate-700',
  CANCELLED: 'bg-red-100 text-red-800',
  NO_SHOW: 'bg-orange-100 text-orange-800',
};

type Props = {
  userId: string | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
};

export default function CustomerDetailModal({ userId, open, onOpenChange }: Props) {
  const { t, i18n } = useTranslation();
  const queryClient = useQueryClient();
  const { data: customer, isLoading } = useAdminCustomerDetail(userId ?? undefined);
  const addFlagMutation = useAddCustomerFlag();
  const removeFlagMutation = useRemoveCustomerFlag();

  const [flagType, setFlagType] = useState('');
  const [flagReason, setFlagReason] = useState('');
  const [showHistory, setShowHistory] = useState(false);

  const invalidate = () => {
    queryClient.invalidateQueries({ queryKey: ['adminCustomerDetail', userId] });
    queryClient.invalidateQueries({ queryKey: ['adminCustomers'] });
    queryClient.invalidateQueries({ queryKey: ['customerFlags'] });
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{t('admin.customer_profile')}</DialogTitle>
        </DialogHeader>

        {isLoading ? (
          <div className="space-y-3">
            {Array.from({ length: 4 }).map((_, i) => <Skeleton key={i} className="h-16" />)}
          </div>
        ) : customer ? (
          <div className="space-y-4">
            {/* Customer Info */}
            <Card className="bg-muted/30">
              <CardContent className="p-4">
                <div className="flex items-center gap-2 mb-2">
                  <User className="h-4 w-4 text-muted-foreground" />
                  <span className="font-medium">{customer.firstName} {customer.lastName}</span>
                </div>
                <p className="text-sm text-muted-foreground">{customer.email}</p>
                {customer.phone && <p className="text-sm text-muted-foreground">{customer.phone}</p>}
                {customer.registeredAt && (
                  <p className="text-xs text-muted-foreground mt-1">
                    {t('admin.member_since')} {new Date(customer.registeredAt).toLocaleDateString()}
                  </p>
                )}

                {/* Stats */}
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mt-3">
                  <div className="text-center">
                    <p className="text-lg font-bold">{customer.totalBookings}</p>
                    <p className="text-xs text-muted-foreground">{t('admin.total_bookings')}</p>
                  </div>
                  <div className="text-center">
                    <p className={`text-lg font-bold ${customer.noShowCount > 0 ? 'text-red-600' : ''}`}>{customer.noShowCount}</p>
                    <p className="text-xs text-muted-foreground">{t('admin.col_no_shows')}</p>
                  </div>
                  <div className="text-center">
                    <p className="text-lg font-bold font-mono">{formatCurrency(customer.totalSpent, 'CAD', i18n.language)}</p>
                    <p className="text-xs text-muted-foreground">{t('admin.col_total_spent')}</p>
                  </div>
                  <div className="text-center">
                    <p className="text-lg font-bold font-mono">{formatCurrency(customer.walletBalance, 'CAD', i18n.language)}</p>
                    <p className="text-xs text-muted-foreground">{t('admin.credit_balance')}</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Flag Management */}
            <Card className="border-l-4 border-l-amber-500">
              <CardContent className="p-4 space-y-3">
                <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                  <Flag className="h-3.5 w-3.5" />
                  {t('admin.flag_management')}
                </div>

                {/* Active flags */}
                <div className="flex flex-wrap gap-2">
                  {customer.activeFlags.length === 0 && (
                    <p className="text-xs text-muted-foreground">{t('admin.no_active_flags')}</p>
                  )}
                  {customer.activeFlags.map((f) => (
                    <Badge key={f.id} className={`text-xs ${FLAG_COLORS[f.flagType] ?? ''}`}>
                      {f.flagType}
                      {f.autoFlagged && <span className="ml-1 opacity-70">({t('admin.auto')})</span>}
                      <button
                        onClick={() => removeFlagMutation.mutate(f.id, {
                          onSuccess: () => { toast.success(t('admin.flag_removed')); invalidate(); },
                        })}
                        className="ml-1.5 hover:opacity-70"
                      >
                        <X className="h-2.5 w-2.5" />
                      </button>
                    </Badge>
                  ))}
                </div>

                {/* Add flag */}
                <div className="space-y-2">
                  <div className="flex items-center gap-2">
                    <select
                      value={flagType}
                      onChange={(e) => setFlagType(e.target.value)}
                      className="h-8 w-44 rounded-lg border border-input bg-transparent px-2 text-xs outline-none"
                    >
                      <option value="">{t('admin.add_flag')}</option>
                      {FLAG_TYPES.map((ft) => (
                        <option key={ft} value={ft}>{ft}</option>
                      ))}
                    </select>
                  </div>
                  {typeof flagType === 'string' && flagType.length > 0 && (
                    <div className="flex items-center gap-2">
                      <input
                        type="text"
                        placeholder={t('admin.reason_placeholder')}
                        value={flagReason}
                        onChange={(e) => setFlagReason(e.target.value)}
                        className="flex-1 h-8 rounded-lg border border-input bg-transparent px-2.5 text-xs outline-none placeholder:text-muted-foreground focus-visible:border-ring"
                      />
                      <Button
                        size="sm" variant="outline"
                        onClick={() => {
                          if (!flagType || !customer.userId) return;
                          addFlagMutation.mutate(
                            { userId: customer.userId, flagType, reason: flagReason },
                            { onSuccess: () => { toast.success(t('admin.flag_added')); setFlagType(''); setFlagReason(''); invalidate(); } },
                          );
                        }}
                        disabled={addFlagMutation.isPending}
                      >
                        {t('admin.add_flag')}
                      </Button>
                    </div>
                  )}
                </div>

                {/* Flag history */}
                {customer.flagHistory.length > 0 && (
                  <>
                    <button
                      onClick={() => setShowHistory(!showHistory)}
                      className="flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground"
                    >
                      {t('admin.flag_history')} ({customer.flagHistory.length})
                      {showHistory ? <ChevronUp className="h-3 w-3" /> : <ChevronDown className="h-3 w-3" />}
                    </button>
                    {showHistory && (
                      <div className="space-y-1 text-xs">
                        {customer.flagHistory.map((f) => (
                          <div key={f.id} className="flex items-center gap-2 py-1 border-b last:border-0">
                            <Badge variant="secondary" className={`text-[10px] ${f.active ? FLAG_COLORS[f.flagType] ?? '' : 'opacity-50 line-through'}`}>
                              {f.flagType}
                            </Badge>
                            <span className="text-muted-foreground">
                              {f.autoFlagged ? t('admin.auto') : ''} {f.reason || ''}
                            </span>
                            {!f.active && f.resolvedAt && (
                              <span className="text-muted-foreground ml-auto">
                                {t('admin.removed_by')} {f.resolvedByName || t('admin.system')} — {new Date(f.resolvedAt).toLocaleDateString()}
                              </span>
                            )}
                          </div>
                        ))}
                      </div>
                    )}
                  </>
                )}
              </CardContent>
            </Card>

            {/* Recent Bookings */}
            {customer.recentBookings.length > 0 && (
              <Card>
                <CardContent className="p-4">
                  <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground mb-3">
                    {t('admin.recent_bookings')}
                  </p>
                  <div className="space-y-2">
                    {customer.recentBookings.map((b) => (
                      <div key={b.id} className="flex items-center justify-between text-sm py-1 border-b last:border-0">
                        <div>
                          <span className="font-medium">{b.sportName}</span>
                          {b.unitName && <span className="text-muted-foreground ml-1">· {b.unitName}</span>}
                          <span className="text-muted-foreground ml-2 text-xs">{b.date}</span>
                        </div>
                        <div className="flex items-center gap-2">
                          <Badge variant="secondary" className={`text-[10px] ${STATUS_COLORS[b.status] ?? ''}`}>
                            {b.status}
                          </Badge>
                          <span className="font-mono text-xs">{formatCurrency(b.amount, 'CAD', i18n.language)}</span>
                        </div>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}
          </div>
        ) : null}
      </DialogContent>
    </Dialog>
  );
}
